import { faPlusCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Auth } from 'aws-amplify';
import cx from 'classnames';
import type { FieldProps } from 'formik';
import { Field, Formik } from 'formik';
import { useContext, useEffect, useState } from 'react';
import { Alert, Button, Form } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import * as Yup from 'yup';

import { getRegion } from '../../api/Location';
import { createUserTrace } from '../../api/Optimise';
import { OnboardingContext } from '../../context/OnboardingContext';
import { usePersistedState } from '../../hooks';
import ErrorLabel from '../../ui/ErrorLabel';
import LoopSpinner from '../../ui/LoopSpinner';
import { constructAddressId } from '../../utils/addressHelperUtil';
import ensureError from '../../utils/ensureError';

const StyledButton = styled(Button)`
  padding-left: 0;
  margin-top: 0;
  margin-bottom: 10px;
  text-decoration: none;
`;

interface ManualAddressFormValues {
  flatNumber: string;
  houseNumber: string;
  houseName: string;
  addressLine1: string;
  addressLine2: string;
  town: string;
  county: string;
  postcode: string;
}

interface AlertState {
  variant: string;
  message: string;
}

const ManualAddressContainer = () => {
  const navigate = useNavigate();
  const { setIsManualAddress, setProgressPercentage } = useContext(OnboardingContext);

  const postcodeValidationRegex =
    '^([Gg][Ii][Rr]' +
    ' 0[Aa]{2})|((([A-Za-z][0-9]{1,2})|(([A-Za-z][A-Ha-hJ-Yj-y]' +
    '[0-9]{1,2})|(([A-Za-z][0-9][A-Za-z])|([A-Za-z][A-Ha-hJ-Yj-y]' +
    '[0-9]?[A-Za-z])))) ?[0-9][A-Za-z]{2})$';

  const addressFieldRegex = '^.{0,50}$';

  const addressFieldSchema = Yup.object().shape({
    postcode: Yup.string()
      .test('len', 'Please enter a UK postcode', (val) => (val?.length ?? 0) >= 6)
      .matches(new RegExp(postcodeValidationRegex), 'Please enter a UK postcode')
      .required('Please enter your postcode'),
    flatNumber: Yup.string().matches(new RegExp(addressFieldRegex), 'Flat Number must be fewer than 50 characters'),
    houseNumber: Yup.string().matches(new RegExp(addressFieldRegex), 'House Number must be fewer than 50 characters'),
    houseName: Yup.string().matches(new RegExp(addressFieldRegex), 'House name must be fewer than 50 characters'),
    addressLine1: Yup.string()
      .matches(new RegExp(addressFieldRegex), 'Address must be fewer than 50 characters')
      .required('Address is required'),
    addressLine2: Yup.string().matches(
      new RegExp(addressFieldRegex),
      'Address line 2 must be fewer than 50 characters'
    ),
    town: Yup.string()
      .matches(new RegExp(addressFieldRegex), 'Town name must be fewer than 50 characters')
      .required('Town/City is required'),
    county: Yup.string()
      .matches(new RegExp(addressFieldRegex), 'County name must be fewer than 50 characters')
      .required('County is required'),
  });

  const [postcode, setPostcode] = usePersistedState('postcode', '');

  const onSubmitManualAddress = async ({
    flatNumber,
    houseNumber,
    houseName,
    addressLine1,
    addressLine2,
    town,
    county,
    postcode,
  }: ManualAddressFormValues) => {
    setShowAlert(false);
    setPostcode(postcode);

    try {
      await getRegion(postcode.toUpperCase());

      const address = constructAddressId(
        flatNumber,
        houseNumber,
        houseName,
        addressLine1,
        addressLine2,
        town,
        county,
        postcode
      );

      const user = await Auth.currentAuthenticatedUser();
      const attributes = {
        address,
        'custom:postcode': postcode,
      };
      await Auth.updateUserAttributes(user, attributes);

      setIsManualAddress(true);

      void createUserTrace('Manual Address', {
        postcode,
      });
      void navigate(`/onboarding/smart-tariff`);
    } catch (submitManualAddressError) {
      let parsedManualAddressError: { message?: string } | undefined;
      let jsonParseError = false;
      try {
        parsedManualAddressError = JSON.parse(ensureError(submitManualAddressError).message) as { message?: string };
      } catch {
        jsonParseError = true;
      }
      if (!jsonParseError && parsedManualAddressError?.message) {
        addAlert('danger', parsedManualAddressError.message);
      } else {
        void navigate(`/onboarding/system-unavailable`);
      }
    }
  };

  const [showAlert, setShowAlert] = useState(false);
  const [showAddressLine2, setShowAddressLine2] = useState(false);
  const [alert, setAlert] = useState<AlertState | undefined>(undefined);

  useEffect(() => {
    setProgressPercentage(10);
  }, [setProgressPercentage]);

  const addAlert = (variant: string, message: string): void => {
    setShowAlert(true);
    setAlert({ variant, message });
    window.scrollTo(0, 0);
  };

  const hideAlert = (): void => {
    setShowAlert(false);
    setAlert(undefined);
  };

  return (
    <>
      <h1 className="mb-3">What's your address?</h1>
      <div>
        <>
          <p>Please enter your address. The smart meter you are connecting to must be at this address.</p>
          <div className="text-left">
            <Formik
              validationSchema={addressFieldSchema}
              onSubmit={onSubmitManualAddress}
              initialValues={{
                flatNumber: '',
                houseNumber: '',
                houseName: '',
                addressLine1: '',
                addressLine2: '',
                town: '',
                county: '',
                postcode: postcode,
              }}
              validateOnChange
            >
              {({ handleSubmit, isValid, isSubmitting }) => (
                <Form onSubmit={handleSubmit}>
                  {showAlert && alert && (
                    <div>
                      <Alert variant={alert.variant} onClose={() => hideAlert()} dismissible>
                        {alert.message}
                      </Alert>
                    </div>
                  )}

                  <Field name="flatNumber">
                    {({ field, meta }: FieldProps<string>) => (
                      <Form.Group controlId="flatNumber" className="text-left">
                        <Form.Label>Flat Number</Form.Label> (optional)
                        <Form.Control
                          type="text"
                          value={field.value}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                          placeholder="Flat number"
                          className={cx({ error: !!meta.error })}
                        />
                        {meta.error && meta.touched && <ErrorLabel label={meta.error} />}
                      </Form.Group>
                    )}
                  </Field>

                  <Field name="houseNumber">
                    {({ field, meta }: FieldProps<string>) => (
                      <Form.Group controlId="houseNumber" className="text-left">
                        <Form.Label>House Number</Form.Label> (optional)
                        <Form.Control
                          type="text"
                          value={field.value}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                          placeholder="House number"
                          className={cx({ error: !!meta.error })}
                        />
                        {meta.error && meta.touched && <ErrorLabel label={meta.error} />}
                      </Form.Group>
                    )}
                  </Field>

                  <Field name="houseName">
                    {({ field, meta }: FieldProps<string>) => (
                      <Form.Group controlId="houseName" className="text-left">
                        <Form.Label>House Name</Form.Label> (optional)
                        <Form.Control
                          type="text"
                          value={field.value}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                          placeholder="House name"
                          className={cx({ error: !!meta.error })}
                        />
                        {meta.error && meta.touched && <ErrorLabel label={meta.error} />}
                      </Form.Group>
                    )}
                  </Field>

                  <Field name="addressLine1">
                    {({ field, meta }: FieldProps<string>) => (
                      <Form.Group
                        controlId="addressLine1"
                        className={cx({
                          'text-left': true,
                          'mb-0': !showAddressLine2,
                        })}
                      >
                        <Form.Label>Address</Form.Label>
                        <Form.Control
                          type="text"
                          value={field.value}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                          placeholder="Address"
                          className={cx({ error: !!meta.error })}
                        />
                        {meta.error && meta.touched && <ErrorLabel label={meta.error} />}
                      </Form.Group>
                    )}
                  </Field>

                  {showAddressLine2 ? (
                    <Field name="addressLine2">
                      {({ field, meta }: FieldProps<string>) => (
                        <Form.Group controlId="addressLine2" className="text-left">
                          <Form.Label>Address Line 2</Form.Label> (optional)
                          <Form.Control
                            type="text"
                            value={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            placeholder="Address Line 2"
                            className={cx({ error: !!meta.error })}
                          />
                          {meta.error && meta.touched && <ErrorLabel label={meta.error} />}
                        </Form.Group>
                      )}
                    </Field>
                  ) : (
                    <StyledButton variant="link" onClick={() => setShowAddressLine2(true)}>
                      <FontAwesomeIcon icon={faPlusCircle} size={'sm'} />
                      &nbsp;Add address line 2
                    </StyledButton>
                  )}

                  <Field name="town">
                    {({ field, meta }: FieldProps<string>) => (
                      <Form.Group controlId="town" className="text-left">
                        <Form.Label>Town/City</Form.Label>
                        <Form.Control
                          type="text"
                          value={field.value}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                          placeholder="Town/City"
                          className={cx({ error: !!meta.error })}
                        />
                        {meta.error && meta.touched && <ErrorLabel label={meta.error} />}
                      </Form.Group>
                    )}
                  </Field>

                  <Field name="county">
                    {({ field, meta }: FieldProps<string>) => (
                      <Form.Group controlId="county" className="text-left">
                        <Form.Label>County</Form.Label>
                        <Form.Control
                          type="text"
                          value={field.value}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                          placeholder="County"
                          className={cx({ error: !!meta.error })}
                        />
                        {meta.error && meta.touched && <ErrorLabel label={meta.error} />}
                      </Form.Group>
                    )}
                  </Field>

                  <Field name="postcode">
                    {({ field, meta }: FieldProps<string>) => (
                      <Form.Group controlId="postcode" className="text-left">
                        <Form.Label>Postcode</Form.Label>
                        <Form.Control
                          type="text"
                          value={field.value}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                          placeholder="Postcode"
                          className={cx({ error: !!meta.error })}
                        />
                        {meta.error && meta.touched && <ErrorLabel label={meta.error} />}
                      </Form.Group>
                    )}
                  </Field>

                  <Form.Group className="mb-4">
                    <div>
                      <Form.Label>Country</Form.Label>
                    </div>
                    <div>UK</div>
                  </Form.Group>

                  <Form.Group>
                    {!isSubmitting ? (
                      <Button variant="primary" disabled={!isValid || isSubmitting} className="w-100" type="submit">
                        Continue
                      </Button>
                    ) : (
                      <LoopSpinner />
                    )}
                  </Form.Group>
                </Form>
              )}
            </Formik>
          </div>
        </>
      </div>
    </>
  );
};

export default ManualAddressContainer;
